import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'dws_theme';

/**
 * Light/dark theme for the WhatsApp palette.
 *
 * Tailwind is configured with darkMode: ["class"], so everything hangs off a
 * `dark` class on <html>; the wa-* tokens in index.css resolve to their dark
 * values from there. Defaults to the operating system preference until the user
 * makes an explicit choice, which is then remembered.
 */
const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

const readStoredTheme = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  // Keeps the mobile browser chrome in step with the app surface.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#111b21' : '#008069');
};

export default function useTheme() {
  const [theme, setTheme] = useState(() => readStoredTheme() || (prefersDark() ? 'dark' : 'light'));

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow the system while the user has not chosen explicitly.
  useEffect(() => {
    if (readStoredTheme()) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => setTheme(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const setAndStore = useCallback((next) => {
    setTheme(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private browsing: the choice just will not persist.
    }
  }, []);

  const toggleTheme = useCallback(
    () => setAndStore(theme === 'dark' ? 'light' : 'dark'),
    [theme, setAndStore]
  );

  return { theme, isDark: theme === 'dark', setTheme: setAndStore, toggleTheme };
}
